import styled from 'styled-components';
import { pxToRem } from 'utils/convert';
import { Avatar } from 'ui/avatar';
import { colors, linkColors, spacing } from 'ui/theme';
import { body2, caption, fontSizes, link2 } from 'ui/theme/typography';

export const RowLayout = styled.div`
  display: grid;
  column-gap: ${spacing.base};
  grid-template-columns: ${pxToRem(30)} ${pxToRem(50)} auto ${pxToRem(30)};
  padding: ${spacing.base} 40px; /** hard coded to page layout */

  > * {
    align-self: center;
  }
`;

export const HeaderRowLayout = styled(RowLayout)`
  border-top: ${spacing.single} solid ${colors.grey15};
  border-bottom: solid ${spacing.single} ${colors.grey15};
  padding-top: ${spacing.half};
  padding-bottom: ${spacing.half};
`;

export const BodyRowLayout = styled(RowLayout)`
  background: ${colors.white};

  &:nth-child(even) {
    background: ${colors.grey1};
  }
`;

/**
  The list layout may or may not include the locales column based on 
  user context. 
 */
const gridWithLocales = `2.75fr minmax(80px, 1fr) 3.25fr ${pxToRem(200)} ${pxToRem(60)}`;
const gridWithoutLocales = `2.75fr minmax(80px, 1fr) 3.25fr ${pxToRem(200)}`;

type InteriorLayoutProps = {
  showLocales: boolean;
};
export const InteriorLayout = styled.div<InteriorLayoutProps>`
  ${body2}
  column-gap: ${spacing.plusHalf};
  display: grid;
  grid-template-columns: ${({ showLocales }) =>
    showLocales ? gridWithLocales : gridWithoutLocales};
  height: 100%;

  > * {
    align-items: center;
    display: flex;
    justify-content: flex-start;
    overflow: hidden;
  }
`;

type MainCellProps = { isHidden?: boolean; isHorizontal?: boolean };
export const MainCell = styled.div<MainCellProps>`
  width: 100%;
  display: flex;
  align-items: space-between;
  flex-direction: ${({ isHorizontal }) => (isHorizontal ? 'row' : 'column')};
  visibility: ${({ isHidden }) => (isHidden ? 'hidden' : 'visible')};
`;

export const LeftContent = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-grow: 1;
`;

export const RightContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const SecondaryCell = styled(MainCell)`
  align-items: ${({ align }: { align?: string }) => align || 'flex-start'};
  flex-direction: column;
  justify-content: center;
`;

export const ModifiedCell = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

export const LocaleCell = styled.div`
  text-wrap: nowrap;
  color: ${colors.textPrimary};
  padding-right: ${spacing.eighth};
`;

export const Name = styled.a`
  ${link2}
  color: ${colors.textPrimary};
  margin-bottom: ${spacing.quarter};
  font-weight: 400;
  text-decoration-color: transparent;

  && {
    &:hover {
      color: ${linkColors.linkHover};
    }

    &:active {
      color: ${linkColors.linkActive};
    }

    &:focus {
      color: ${linkColors.linkFocus};
      outline: none;
    }
  }
`;

export const IdDisplay = styled.div`
  ${body2}
  color: ${colors.textSecondary};
  font-size: ${fontSizes.s12};
`;

export const CreatedDisplay = styled.div`
  ${caption}
  color: ${colors.textSecondary};
`;

export const UpdatedDisplay = styled.div`
  ${body2}
`;

export const SecondaryText = styled.div`
  color: ${colors.textSecondary};
`;

export const StyledAvatar = styled(Avatar)`
  min-width: ${pxToRem(32)};
  margin-right: ${spacing.base};
`;

export const LocalesList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.quarter};
`;

export const LocaleRow = styled(Row)`
  margin-top: ${spacing.threeQuarter};
`;
